/*
  Communite collection controller
  Keeps Shopify filter URLs progressive, updates one section at a time and supports editor reloads.
*/

(() => {
  const ROOT_SELECTOR = '[data-cm-collection]'
  const controllers = new WeakMap()

  const findRoots = (root) => {
    const roots = []

    if (root instanceof Element && root.matches(ROOT_SELECTOR)) {
      roots.push(root)
    }

    root.querySelectorAll?.(ROOT_SELECTOR).forEach(element => roots.push(element))
    return roots
  }

  const isModifiedClick = (event) => {
    return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
  }

  class CommuniteCollectionController {
    constructor (root) {
      this.root = root
      this.sectionId = root.dataset.sectionId
      this.abortController = new AbortController()
      this.signal = this.abortController.signal
      this.fetchController = null
      this.retryUrl = null
      this.retryOptions = null

      this.root.addEventListener('click', event => this.handleClick(event), { signal: this.signal })
      this.root.addEventListener('change', event => this.handleChange(event), { signal: this.signal })
      this.root.addEventListener('submit', event => this.handleSubmit(event), { signal: this.signal })
    }

    handleClick (event) {
      const retryButton = event.target.closest('[data-cm-collection-retry]')

      if (retryButton && this.root.contains(retryButton)) {
        event.preventDefault()
        if (this.retryUrl) this.load(this.retryUrl, this.retryOptions)
        return
      }

      const link = event.target.closest('a[data-cm-collection-url]')

      if (!link || !this.root.contains(link) || isModifiedClick(event)) return

      event.preventDefault()
      this.load(link.href, { push: true, scroll: true, focus: true })
    }

    handleChange (event) {
      const sortForm = event.target.closest('[data-cm-sort-form]')

      if (sortForm && this.root.contains(sortForm)) {
        this.requestFormUpdate(sortForm)
        return
      }

      const filterForm = event.target.closest('[data-cm-filter-form][data-cm-filter-auto]')

      if (filterForm && this.root.contains(filterForm)) {
        this.requestFormUpdate(filterForm)
      }
    }

    handleSubmit (event) {
      const form = event.target.closest('[data-cm-filter-form], [data-cm-sort-form]')

      if (!form || !this.root.contains(form)) return

      event.preventDefault()
      this.closeDialog()
      this.requestFormUpdate(form)
    }

    requestFormUpdate (form) {
      const url = new URL(form.action, window.location.origin)
      const params = new URLSearchParams()

      for (const [key, value] of new FormData(form).entries()) {
        if (String(value).trim() !== '') params.append(key, value)
      }

      params.delete('page')
      url.search = params.toString()
      this.load(url, { push: true, scroll: false, focus: true })
    }

    closeDialog () {
      const openDialog = this.root.querySelector('[data-cm-dialog]:not([hidden])')
      openDialog?.querySelector('[data-cm-dialog-close]')?.click()
    }

    setLoading (isLoading) {
      this.root.toggleAttribute('aria-busy', isLoading)

      const loadingIndicator = this.root.querySelector('[data-cm-collection-loading]')
      if (loadingIndicator) loadingIndicator.hidden = !isLoading

      if (isLoading) {
        this.hideError()
        this.announce(this.root.dataset.loadingMessage)
      }
    }

    announce (message) {
      const liveRegion = this.root.querySelector('[data-cm-collection-live]')
      if (!liveRegion || !message) return

      liveRegion.textContent = ''
      window.requestAnimationFrame(() => {
        liveRegion.textContent = message
      })
    }

    hideError () {
      this.root.querySelector('[data-cm-collection-error]')?.setAttribute('hidden', 'hidden')
    }

    showError () {
      const error = this.root.querySelector('[data-cm-collection-error]')
      const message = this.root.dataset.errorMessage

      if (error) {
        const messageElement = error.querySelector('[data-cm-collection-error-text]')
        if (messageElement && message) messageElement.textContent = message
        error.removeAttribute('hidden')
      }

      this.announce(message)
      error?.querySelector('[data-cm-collection-retry]')?.focus()
    }

    async load (urlValue, options = {}) {
      const optionsWithDefaults = {
        push: false,
        scroll: false,
        focus: false,
        ...options
      }
      const storefrontUrl = new URL(urlValue, window.location.origin)
      const requestUrl = new URL(storefrontUrl)
      requestUrl.searchParams.set('section_id', this.sectionId)

      this.fetchController?.abort()
      this.fetchController = new AbortController()
      this.retryUrl = storefrontUrl
      this.retryOptions = optionsWithDefaults
      this.setLoading(true)

      try {
        const response = await fetch(requestUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: this.fetchController.signal
        })

        if (!response.ok) throw new Error(`Collection request failed: ${response.status}`)

        const html = await response.text()
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html')
        const nextRoot = [...parsedDocument.querySelectorAll(ROOT_SELECTOR)]
          .find(root => root.dataset.sectionId === this.sectionId)

        if (!nextRoot) throw new Error('Collection section missing from response')

        if (optionsWithDefaults.push) {
          window.history.pushState({ cmCollection: true }, '', storefrontUrl)
        }

        window.CommuniteUI?.destroy(this.root)
        const previousRoot = this.root
        this.destroy()
        previousRoot.replaceWith(nextRoot)
        window.CommuniteUI?.init(nextRoot)
        init(nextRoot)
        nextRoot.dispatchEvent(new CustomEvent('cm:content:updated', { bubbles: true }))

        const nextController = controllers.get(nextRoot)
        const results = nextRoot.querySelector('[data-cm-results]')
        nextController?.announce(results?.dataset.resultsMessage)

        if (optionsWithDefaults.scroll) {
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
          nextRoot.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'start'
          })
        }

        if (optionsWithDefaults.focus) {
          const focusTarget = nextRoot.querySelector('[data-cm-result-count]') ||
            nextRoot.querySelector('[data-cm-results] a, [data-cm-results] button')

          if (focusTarget) {
            focusTarget.setAttribute('tabindex', '-1')
            window.requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }))
          }
        }

        window.dispatchEvent(new CustomEvent('cm:collection:updated', {
          detail: { sectionId: this.sectionId, url: storefrontUrl.toString() }
        }))
      } catch (error) {
        if (error.name === 'AbortError') return

        this.setLoading(false)
        this.showError()
      }
    }

    destroy () {
      this.fetchController?.abort()
      this.abortController.abort()
      controllers.delete(this.root)
    }
  }

  const init = (root = document) => {
    findRoots(root).forEach(element => {
      if (controllers.has(element)) return
      controllers.set(element, new CommuniteCollectionController(element))
    })
  }

  const destroy = (root) => {
    findRoots(root).forEach(element => controllers.get(element)?.destroy())
  }

  document.addEventListener('shopify:section:load', event => init(event.target))
  document.addEventListener('shopify:section:unload', event => destroy(event.target))
  document.addEventListener('shopify:section:select', event => init(event.target))
  window.addEventListener('popstate', () => {
    findRoots(document).forEach(root => {
      controllers.get(root)?.load(window.location.href, {
        push: false,
        scroll: false,
        focus: false
      })
    })
  })

  init(document)
})()
